import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { CircleNotchIcon, KeyIcon } from "@phosphor-icons/react";
import { passwordIssue, PASSWORD_RULE } from "@/lib/password";

const inputCls = "h-10 rounded-sm bg-zinc-900 border-zinc-800 font-mono text-sm text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-1 focus-visible:ring-cyan-500";

export const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) return toast.error("New passwords do not match");
    const pwIssue = passwordIssue(next);
    if (pwIssue) return toast.error(pwIssue);
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, "Could not change password"));
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-head"><KeyIcon className="h-5 w-5 text-cyan-400" /> Change password</DialogTitle>
          <DialogDescription className="text-zinc-500">Enter your current password and choose a new one.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <label className="text-xs text-zinc-400">Current password</label>
            <Input data-testid="cp-current" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-zinc-400">New password</label>
            <Input data-testid="cp-new" type="password" required value={next} onChange={(e) => setNext(e.target.value)} placeholder="Min 8 chars, 1 uppercase, 1 special" className={inputCls} />
            <p className="text-[11px] text-zinc-600">{PASSWORD_RULE}</p>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-zinc-400">Confirm new password</label>
            <Input data-testid="cp-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
          </div>
          <button data-testid="cp-submit" disabled={busy}
            className="flex w-full items-center justify-center gap-2 rounded-sm bg-cyan-500 hover:bg-cyan-400 py-2.5 text-sm font-semibold text-zinc-950 transition-colors disabled:opacity-60">
            {busy ? <CircleNotchIcon className="h-4 w-4 animate-spin" /> : "Update password"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
